import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { SistemaEnum } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { IPermissionCheck } from '../models/user-permissions.interface';

@Injectable({
  providedIn: 'root',
})
export class PermissionValidatorService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);

  // ✅ Apenas 1 sistema ativo por vez
  private _currentSistema = signal<SistemaEnum | null>(null);
  private _sistemaNome = signal<string>('');
  private _permissions = signal<string[]>([]);
  private _loading = signal(false);

  // Signals públicos (read-only)
  $currentSistema = this._currentSistema.asReadonly();
  $sistemaNome = this._sistemaNome.asReadonly();
  $permissions = this._permissions.asReadonly();
  $loading = this._loading.asReadonly();

  private baseUrl = `${this.environment.getApiUrl('nomoh')}/auth`;

  // Computed: permissões carregadas?
  $permissionsLoaded = computed(() => this._permissions().length > 0);

  /**
   * ✅ Carregar permissões do usuário para 1 sistema específico
   * Chamado ao fazer login ou trocar de sistema
   */
  async loadPermissionsForSystem(sistema: SistemaEnum): Promise<void> {
    // Verificar cache
    const cached = this.getFromCache(sistema);
    if (cached) {
      this._permissions.set(cached.permissions);
      this._currentSistema.set(cached.sistema);
      this._sistemaNome.set(cached.sistemaNome);
      return;
    }

    this._loading.set(true);
    try {
      let permissions: string[];
      let sistemaNome: string;

      if (sistema === SistemaEnum.Bardo) {
        const bardoUrl = `${this.environment.getApiUrlWithEnum(SistemaEnum.Bardo)}/auth/bardo/me/permissions`;
        const response = await firstValueFrom(
          this.http.get<{ sistemaNome: string; permissions: string[] }>(bardoUrl)
        );
        permissions = response.permissions;
        sistemaNome = response.sistemaNome;
      } else {
        const response = await firstValueFrom(
          this.http.get<{ sistema: SistemaEnum; sistemaNome: string; permissions: string[] }>(
            `${this.baseUrl}/me/permissions?sistema=${sistema}`
          )
        );
        permissions = response.permissions;
        sistemaNome = response.sistemaNome;
      }

      this._permissions.set(permissions);
      this._currentSistema.set(sistema);
      this._sistemaNome.set(sistemaNome);
      this.saveToCache({ sistema, sistemaNome, permissions });
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      this._permissions.set([]);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * ✅ Verificar se usuário tem permissão específica
   */
  hasPermission(permissionKey: string): boolean {
    return this._permissions().includes(permissionKey);
  }

  /**
   * ✅ Verificar se usuário tem TODAS as permissões
   */
  hasAllPermissions(permissionKeys: string[]): boolean {
    return permissionKeys.every((key) => this.hasPermission(key));
  }

  /**
   * ✅ Verificar se usuário tem ALGUMA das permissões
   */
  hasAnyPermission(permissionKeys: string[]): boolean {
    return permissionKeys.some((key) => this.hasPermission(key));
  }

  /**
   * ✅ Verificar permissão com detalhes (para debugging)
   */
  checkPermission(permissionKey: string): IPermissionCheck {
    if (!this.$permissionsLoaded()) {
      return { hasPermission: false, reason: 'permissions_not_loaded' };
    }

    const hasPermission = this.hasPermission(permissionKey);

    return {
      hasPermission,
      reason: hasPermission ? undefined : 'missing_permission',
    };
  }

  /**
   * ✅ Computed Signal Factory (para uso em templates)
   */
  $hasPermission(permissionKey: string) {
    return computed(() => this.hasPermission(permissionKey));
  }

  /**
   * ✅ Definir permissões diretamente a partir do response de login/refresh,
   * sem disparar uma requisição HTTP extra.
   */
  setFromLoginResponse(sistema: SistemaEnum, sistemaNome: string, permissions: string[]): void {
    this._permissions.set(permissions);
    this._currentSistema.set(sistema);
    this._sistemaNome.set(sistemaNome);
    this.saveToCache({ sistema, sistemaNome, permissions });
  }

  /**
   * ✅ Limpar permissões (logout ou troca de sistema)
   */
  clearPermissions(): void {
    this._permissions.set([]);
    this._currentSistema.set(null);
    this._sistemaNome.set('');
    this.clearCache();
  }

  // ========== CACHE (sessionStorage - por sistema) ==========

  private CACHE_DURATION = 15 * 60 * 1000; // 15 minutos

  private getFromCache(
    sistema: SistemaEnum
  ): {
    sistema: SistemaEnum;
    sistemaNome: string;
    permissions: string[];
  } | null {
    try {
      const key = `permissions_sistema_${sistema}`;
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;

      const data = JSON.parse(cached);

      if (!data.permissions || data.permissions.length === 0) {
        sessionStorage.removeItem(key);
        return null;
      }

      const age = Date.now() - new Date(data.loadedAt).getTime();

      if (age > this.CACHE_DURATION) {
        sessionStorage.removeItem(key);
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  private saveToCache(data: {
    sistema: SistemaEnum;
    sistemaNome: string;
    permissions: string[];
  }): void {
    try {
      const key = `permissions_sistema_${data.sistema}`;
      const cacheData = {
        ...data,
        loadedAt: new Date(),
      };
      sessionStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Erro ao salvar cache de permissões:', error);
    }
  }

  private clearCache(): void {
    // Limpar cache de todos os sistemas
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('permissions_sistema_')) {
        sessionStorage.removeItem(key);
      }
    }
  }
}
